import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { 
  ArrowRight, 
  CheckCircle, 
  Stethoscope, 
  ShoppingCart, 
  GraduationCap, 
  Rocket, 
  Settings,
  Users,
  Shield,
  Clock,
  Award
} from 'lucide-react';
import Newsletter from '../components/Newsletter';

const services = [
  {
    icon: Stethoscope,
    title: 'Technical Consultation',
    description: 'Expert guidance on healthcare IT strategy, infrastructure planning, and digital transformation.',
    image: 'https://images.unsplash.com/photo-1706777268519-f7aa1898b823?auto=format&fit=crop&q=80&w=800',
  },
  {
    icon: ShoppingCart,
    title: 'Procurement',
    description: 'Strategic sourcing and acquisition of medical technology solutions tailored to your needs.',
    image: 'https://images.unsplash.com/photo-1606206873764-fd15e242df52?auto=format&fit=crop&q=80&w=800',
  },
  {
    icon: GraduationCap,
    title: 'Training',
    description: 'Comprehensive staff training programs for seamless technology adoption and efficiency.',
    image: 'https://images.unsplash.com/photo-1531482615713-2afd69097998?auto=format&fit=crop&q=80&w=800',
  },
  {
    icon: Rocket,
    title: 'Deployment',
    description: 'End-to-end implementation services ensuring smooth rollout of healthcare systems.',
    image: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&q=80&w=800',
  },
  {
    icon: Settings,
    title: 'Management',
    description: '24/7 support and management of your healthcare IT infrastructure for optimal performance.',
    image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&q=80&w=800',
  },
];

const stats = [
  { value: '500+', label: 'Healthcare Partners' },
  { value: '98%', label: 'Client Satisfaction' },
  { value: '15+', label: 'Years Experience' },
  { value: '24/7', label: 'Support Available' },
];

const whyChooseUs = [
  { icon: Shield, title: 'HIPAA Compliant', description: 'All solutions meet healthcare regulatory standards' },
  { icon: Clock, title: 'Fast Implementation', description: 'Rapid deployment without disrupting operations' },
  { icon: Users, title: 'Dedicated Support', description: 'Expert team available round the clock' },
  { icon: Award, title: 'Industry Leaders', description: 'Recognized excellence in healthcare tech' },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

export default function Home() {
  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden hero-pattern" data-testid="hero-section">
        {/* Background Blobs */}
        <div className="absolute top-20 left-10 w-96 h-96 blob blob-1 -z-10"></div>
        <div className="absolute bottom-20 right-10 w-80 h-80 blob blob-2 -z-10"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <span className="inline-block text-sm font-medium tracking-wider uppercase text-cyan-600 mb-4">
                Healthcare Technology Solutions
              </span>
              <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.1] mb-6">
                <span className="gradient-text">Innovating</span>
                <br />
                Healthcare Tech
              </h1>
              <p className="text-lg md:text-xl text-slate-600 leading-relaxed mb-8 max-w-xl">
                CureClave Innovations empowers healthcare organizations with cutting-edge technology solutions. From consultation to deployment, we're your trusted partner in digital health transformation.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/contact">
                  <Button 
                    className="btn-gradient text-white rounded-full px-8 py-6 text-base font-semibold shadow-lg shadow-cyan-500/20 hover:scale-105 transition-transform"
                    data-testid="hero-cta-primary"
                  >
                    Get Started
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/services">
                  <Button 
                    variant="outline"
                    className="rounded-full px-8 py-6 text-base font-medium border-slate-200 hover:bg-slate-50"
                    data-testid="hero-cta-secondary"
                  >
                    Our Services
                  </Button>
                </Link>
              </div>

              {/* Trust badges */}
              <div className="flex items-center gap-6 mt-10">
                <div className="flex -space-x-3">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 border-2 border-white"></div>
                  ))}
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-900">500+ Partners</p>
                  <p className="text-xs text-slate-500">Trust CureClave</p>
                </div>
              </div>
            </motion.div>

            {/* Right Content - Hero Image */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-cyan-500/10">
                <img
                  src="https://images.unsplash.com/photo-1578496480240-32d3e0c04525?auto=format&fit=crop&q=80&w=2000"
                  alt="Healthcare Technology"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/30 to-transparent"></div>
              </div>
              
              {/* Floating card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-6 max-w-[240px]"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                  </div>
                  <span className="font-heading font-semibold text-slate-900">HIPAA Compliant</span>
                </div>
                <p className="text-sm text-slate-500">All solutions meet regulatory standards</p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-slate-50" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-4 gap-8"
          >
            {stats.map((stat, index) => (
              <motion.div key={index} variants={itemVariants} className="text-center">
                <p className="font-heading text-4xl md:text-5xl font-bold text-cyan-600 mb-2">
                  {stat.value}
                </p>
                <p className="text-slate-600 font-medium">{stat.label}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 md:py-32" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="text-sm font-medium tracking-wider uppercase text-cyan-600 mb-4 block">
              What We Offer
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold tracking-tight text-slate-900 mb-6">
              Comprehensive Healthcare Solutions
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              End-to-end services designed to transform your healthcare organization's technology infrastructure.
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {services.map((service, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="service-card group relative overflow-hidden rounded-3xl bg-white p-8 border border-slate-100 shadow-sm"
                data-testid={`service-card-${index}`}
              >
                <div className="service-icon w-14 h-14 rounded-2xl bg-cyan-50 flex items-center justify-center text-cyan-600 mb-6 transition-transform">
                  <service.icon className="w-7 h-7" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3">
                  {service.title}
                </h3>
                <p className="text-slate-600 leading-relaxed mb-6">
                  {service.description}
                </p>
                <Link
                  to="/services"
                  className="inline-flex items-center text-cyan-600 font-medium hover:text-cyan-700 transition-colors"
                >
                  Learn more
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </motion.div>
            ))}
          </motion.div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button 
                variant="outline"
                className="rounded-full px-8 py-4 font-medium border-slate-200 hover:bg-slate-50"
                data-testid="view-all-services"
              >
                View All Services
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-slate-50" data-testid="why-choose-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-sm font-medium tracking-wider uppercase text-cyan-600 mb-4 block">
                Why CureClave
              </span>
              <h2 className="font-heading text-4xl md:text-5xl font-semibold tracking-tight text-slate-900 mb-6">
                Your Trusted Healthcare Tech Partner
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                With over 15 years of experience in healthcare technology, we understand the unique challenges faced by healthcare organizations. Our solutions are designed to improve patient care while ensuring compliance and security.
              </p>

              <motion.div
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="grid grid-cols-1 sm:grid-cols-2 gap-6"
              >
                {whyChooseUs.map((item, index) => (
                  <motion.div key={index} variants={itemVariants} className="flex gap-4">
                    <div className="w-12 h-12 rounded-xl bg-cyan-100 flex items-center justify-center text-cyan-600 flex-shrink-0">
                      <item.icon className="w-6 h-6" />
                    </div>
                    <div>
                      <h4 className="font-heading font-semibold text-slate-900 mb-1">{item.title}</h4>
                      <p className="text-sm text-slate-600">{item.description}</p>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="rounded-3xl overflow-hidden shadow-xl">
                <img
                  src="https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&q=80&w=800"
                  alt="Healthcare professionals"
                  className="w-full h-[500px] object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl shadow-xl p-6">
                <p className="font-heading text-3xl font-bold text-cyan-600 mb-1">98%</p>
                <p className="text-sm text-slate-600">Client Satisfaction Rate</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-cyan-500 to-blue-600 p-12 md:p-16 text-center"
          >
            <div className="absolute top-0 left-0 w-full h-full bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-10"></div>
            <div className="relative z-10">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
                Ready to Transform Your Healthcare Technology?
              </h2>
              <p className="text-cyan-100 text-lg max-w-2xl mx-auto mb-8">
                Let's discuss how CureClave Innovations can help modernize your healthcare infrastructure and improve patient outcomes.
              </p>
              <Link to="/contact">
                <Button 
                  className="bg-white text-cyan-600 hover:bg-slate-50 rounded-full px-8 py-6 text-base font-semibold shadow-lg"
                  data-testid="cta-contact-button"
                >
                  Schedule a Consultation
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Newsletter */}
      <Newsletter />
    </div>
  );
}
